
import * as React from 'react';
import { Omit } from '../../../../utilities/Omit';
import { withWizard, IWithWizardProps, IWithWizardInjectedProps, IDecorateWithWizardProps } from '../../../decorators/withWizard';
import { IItemCacheThunkAction } from '../../../../store/actions/ItemCacheActions';
import { IItemCache } from '../../../../models/IItemCacheObserver';
import { connectToItemCache } from '../../../decorators/connectToItemCache';
import {
  withSelectedItems, withExactlyOneItem, IDecorateWithSelectedItemsProps, IDecorateWithExactlyOneItemProps, withCurrentItemAsFallback
} from '../../../decorators/selectors';
import { shareActionControl, ISharingInformation, ISharingWizardProps } from '../../../../controls/shareActionControl';
import { copyLinkActionControl } from '../../../../controls/copyLinkActionControl';
import { IDecoratedProps } from '../../../../utilities/components/Decorators';
import { getSharingClientId } from '../../../../operations/getSharingClientId';
import { malwareDetectedFacet } from '../../../../facets/ItemFacets';
import { getItemActionEngagement } from '../../../../models/telemetry/getItemEngagement';
import { sharedFieldClickActionControl } from '../../../../controls/sharedFieldClickActionControl';

export interface ISharingCalloutConnectedProps {
  getSharingInformation: () => IItemCacheThunkAction<Promise<ISharingInformation>>;
  sharingWizard: React.ComponentType<ISharingWizardProps>;
}

type IPickSharingWizardProps = Pick<ISharingWizardProps,
  'getSharingInformation' |
  'getClientId' |
  'defaultRecipients' |
  'customLabeledAudienceConfig' |
  'manageAccessAction'>;

export type ISharingCalloutWrapperProps = IDecoratedProps<
  ISharingWizardProps,
  IPickSharingWizardProps,
  ISharingCalloutConnectedProps>;

export type IShareWizardActionProps = IDecorateWithWizardProps<
  ISharingCalloutWrapperProps,
  Omit<IWithWizardInjectedProps, 'onExecuteProps'>,
  Pick<ISharingCalloutWrapperProps, 'mode'>>;

const ShareItemWizard: React.ComponentType<IShareWizardActionProps> =
  connectToItemCache((itemCache: IItemCache, props: ISharingCalloutConnectedProps & { itemKey: string }):
    IPickSharingWizardProps => {
    const { dispatch } = itemCache;

    return {
      getSharingInformation: async () => {
        return await dispatch(props.getSharingInformation());
      },
      getClientId: () => {
        return dispatch(getSharingClientId());
      }
    };
  })(withWizard(undefined, { isBackgroundTask: true })((props: ISharingWizardProps & {
    sharingWizard: React.ComponentType<ISharingWizardProps>;
  }): JSX.Element => {
    const {
      sharingWizard: SharingWizard,
      ...sharingWizardProps
    } = props;

    return (
      <SharingWizard { ...sharingWizardProps } />
    );
  }));

const doNotShare = () => () => Promise.reject(new Error('Cannot share.'));

export type IShareItemWizardInputProps =
  Pick<ISharingCalloutConnectedProps, 'getSharingInformation'> &
  Pick<ISharingWizardProps, 'customLabeledAudienceConfig' | 'defaultRecipients' | 'manageAccessAction'> &
  Pick<IWithWizardProps, 'isAvailable'>;

// The Copy link action is just another mode of the sharing dialog.
// Some properties are common to the share action and copy link action.
function getShareItemWizardProps(
  itemCache: IItemCache,
  props: {
    itemKey: string;
  }
): IShareItemWizardInputProps {
  const {
    itemKey
  } = props;

  const controlHandler = itemCache.demandItemFacet(shareActionControl, itemKey);

  const {
    isAvailable,
    getSharingInformation,
    defaultRecipients,
    customLabeledAudienceConfig
  } = shareActionControl.evaluate(controlHandler)(itemCache, {
    itemKey: itemKey,
    getSharingInformation: doNotShare,
    defaultRecipients: [],
    customLabeledAudienceConfig: undefined,
    isAvailable: false
  });

  const openManageAccessControlHandler = itemCache.demandItemFacet(sharedFieldClickActionControl, itemKey);

  const {
    onClickAction: manageAccessAction
  } = sharedFieldClickActionControl.evaluate(openManageAccessControlHandler)(itemCache, {
    itemKey: itemKey
  });

  return {
    isAvailable,
    getSharingInformation,
    defaultRecipients,
    customLabeledAudienceConfig,
    manageAccessAction
  };
}

export type IItemShareActionProps = IDecoratedProps<
  IShareWizardActionProps,
  IShareItemWizardInputProps & Pick<ISharingCalloutWrapperProps, 'mode'>,
  { itemKey: string; }>;

export const ItemShareAction: React.ComponentType<IItemShareActionProps> = connectToItemCache((
  itemCache: IItemCache,
  props: {
    itemKey: string;
  }
): Pick<IWithWizardProps, 'isAvailable' | 'engagement' | 'getEngagement'> &
  Pick<ISharingCalloutConnectedProps, 'getSharingInformation'> &
  IShareItemWizardInputProps &
  Pick<ISharingCalloutWrapperProps, 'mode'> => {

  const commonProps = getShareItemWizardProps(itemCache, props);
  const isMalwareDetected = itemCache.demandItemFacet(malwareDetectedFacet, props.itemKey);

  return {
    ...commonProps,
    isAvailable: isMalwareDetected ? false : commonProps.isAvailable,
    ...getItemActionEngagement(itemCache, {
      itemKey: props.itemKey,
      engagement: {
        name: 'Share',
        isIntentional: true
      }
    }),
    mode: 1
  };
})(ShareItemWizard);

export type IItemCopyLinkActionProps = IDecoratedProps<
  IShareWizardActionProps,
  IShareItemWizardInputProps & Pick<ISharingCalloutWrapperProps, 'mode'>,
  { itemKey: string; }>;

export const ItemCopyLinkAction: React.ComponentType<IItemCopyLinkActionProps> = connectToItemCache((
  itemCache: IItemCache,
  props: {
    itemKey: string;
  }
): Pick<IWithWizardProps, 'isAvailable' | 'engagement' | 'getEngagement'> &
  Pick<ISharingCalloutConnectedProps, 'getSharingInformation'> &
  IShareItemWizardInputProps &
  Pick<ISharingCalloutWrapperProps, 'mode'> => {
  const {
    itemKey
  } = props;

  const commonProps = getShareItemWizardProps(itemCache, { itemKey: props.itemKey });
  const isMalwareDetected = itemCache.demandItemFacet(malwareDetectedFacet, props.itemKey);
  const controlHandler = itemCache.demandItemFacet(copyLinkActionControl, itemKey);
  const {
    isAvailable
  } = copyLinkActionControl.evaluate(controlHandler)(itemCache, {
    itemKey: itemKey,
    isAvailable: isMalwareDetected ? false : commonProps.isAvailable
  });

  return {
    ...commonProps,
    isAvailable: isAvailable,
    ...getItemActionEngagement(itemCache, {
      itemKey,
      engagement: {
        name: 'CopyLink',
        isIntentional: true
      }
    }),
    mode: 2
  };
})(ShareItemWizard);

export type IShareActionProps = IDecorateWithSelectedItemsProps<IDecorateWithExactlyOneItemProps<IItemShareActionProps>>;

export const ShareAction: React.ComponentType<IShareActionProps> =
  withSelectedItems()(withCurrentItemAsFallback()(withExactlyOneItem()(ItemShareAction)));

export type ICopyLinkActionProps = IDecorateWithSelectedItemsProps<IDecorateWithExactlyOneItemProps<IItemCopyLinkActionProps>>;

export const CopyLinkAction: React.ComponentType<ICopyLinkActionProps> =
  withSelectedItems()(withCurrentItemAsFallback()(withExactlyOneItem()(ItemCopyLinkAction)));
